import * as t from 'io-ts';
import { isUndefined } from 'lodash-es';

import type { ShortcutsDefinition } from 'header/types';

import { define, createSpecSettingsInterface } from './_common';

export interface ShortcutsSettingDefinition extends ShortcutsDefinition {
    key: 'SHORTCUTS';
}

const definition: ShortcutsSettingDefinition = {
    key: 'SHORTCUTS',
    type: 'Shortcuts',
    value: {
        right: [],
        center: [],
        left: [],
    },
    label: 'Shortcuts',
    scope: 'designer',
};

export const ShortcutElementC = t.type({
    key: t.string,
    label: t.string,
    value: t.union([
        t.string,
        t.number,
        t.boolean,
        t.null,
        t.array(
            t.type({
                key: t.string,
                order: t.number,
                drill: t.string,
            }),
        ),
        t.intersection([
            t.type({
                key: t.string,
                value: t.number,
            }),
            t.partial({
                group: t.string,
            }),
        ]),
        t.type({
            from: t.type({
                offset: t.number,
            }),
            to: t.union([
                t.type({
                    offset: t.number,
                }),
                t.null,
            ]),
        }),
        t.type({
            queryId: t.string,
            system: t.string,
        }),
    ]),
});

const specSettingsInterface = createSpecSettingsInterface(
    definition,
    t.type({
        left: t.array(ShortcutElementC),
        center: t.array(ShortcutElementC),
        right: t.array(ShortcutElementC),
    }),
);

export const codec = specSettingsInterface.input;

export const ShortcutsSettingDefinition = define({
    key: 'SHORTCUTS',
    label: 'shortcuts',
    definition,
    fromSetting: (settings, _, view) =>
        // ensure that main config will not be overwritten with computed default
        specSettingsInterface.extractValue(settings.shortcuts) ||
        (view
            ? undefined
            : {
                  left: [],
                  center: [],
                  right: [],
              }),
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: settings =>
        isUndefined(settings.shortcuts)
            ? definition
            : specSettingsInterface.createDefinitionWithSettings(settings.shortcuts),
    replacementTags: definition => ({
        SHORTCUTS: Object.values(definition.value)
            .flatMap(values => values.map(v => v.label))
            .join(' ,'),
        SHORTCUTS_LEFT: definition.value.left.map(v => v.label).join(' ,'),
        SHORTCUTS_CENTER: definition.value.center.map(v => v.label).join(' ,'),
        SHORTCUTS_RIGHT: definition.value.right.map(v => v.label).join(' ,'),
    }),
});
